import React, { useState } from 'react';
import { X, Key, AlertTriangle, Mail, User } from 'lucide-react';
import { resetUserPassword } from '../lib/admin';
import type { UserStats } from '../types/admin';

interface Props {
  user: UserStats;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ResetUserPasswordModal({ user, onClose, onSuccess }: Props) {
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [passwordCopied, setPasswordCopied] = useState(false);

  const expectedConfirmText = 'RESET PASSWORD';
  const isConfirmValid = confirmText === expectedConfirmText;

  const handleReset = async () => {
    if (!isConfirmValid) {
      setError('Please type RESET PASSWORD to confirm');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const result = await resetUserPassword(user.user_id, reason);
      setGeneratedPassword(result.newPassword);
      setSuccess(true);
      
      // Auto close after showing success message
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 10000); // Give more time to copy password
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(generatedPassword);
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy password:', err);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
        <div className="bg-gray-800/95 backdrop-blur-sm rounded-lg p-6 max-w-md w-full">
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="bg-yellow-900/50 p-4 rounded-full">
                <Key className="w-12 h-12 text-yellow-400" />
              </div>
            </div>
            <h3 className="text-xl font-semibold text-white">Password Reset Complete!</h3>
            <p className="text-gray-300">
              New password generated for {user.email}
            </p>
            
            {/* Security Warning */}
            <div className="bg-red-900/30 border border-red-800 p-4 rounded-lg">
              <p className="text-red-200 text-sm font-medium mb-2">
                ⚠️ SECURITY WARNING
              </p>
              <p className="text-red-300 text-xs">
                This password will only be shown once. Copy it securely and share it with the user through a secure channel. The user should change this password immediately after logging in.
              </p>
            </div>

            {/* Generated Password */}
            <div className="bg-gray-900/50 p-4 rounded-lg">
              <p className="text-gray-300 text-sm mb-2">New Password:</p>
              <div className="flex items-center space-x-2">
                <code className="flex-1 px-3 py-2 bg-gray-800 text-white font-mono text-lg rounded border select-all">
                  {generatedPassword}
                </code>
                <button
                  onClick={copyPassword}
                  className={`px-3 py-2 rounded transition-colors ${
                    passwordCopied 
                      ? 'bg-green-600 text-white' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {passwordCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>

            <div className="bg-blue-900/50 p-4 rounded-lg">
              <p className="text-blue-200 text-sm">
                🔐 Share this password with the user securely and instruct them to change it immediately after logging in.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-gray-800/95 backdrop-blur-sm rounded-lg p-6 max-w-2xl w-full max-h-[95vh] overflow-y-auto my-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Key className="w-6 h-6 text-blue-400" />
            <h3 className="text-lg font-semibold text-white">Reset User Password</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-900/50 text-red-200 p-3 rounded-md">
            {error}
          </div>
        )}

        <div className="space-y-6">
          {/* Warning */}
          <div className="bg-red-900/30 border border-red-800 p-4 rounded-lg">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-red-200 mb-2">⚠️ SECURITY WARNING: Direct Password Reset</h4>
                <p className="text-red-300 text-sm mb-2">
                  This action will:
                </p>
                <ul className="text-red-300 text-sm space-y-1 list-disc list-inside">
                  <li><strong>Generate a new password and show it to you</strong></li>
                  <li><strong>Immediately replace their current password</strong></li>
                  <li><strong>Give you access to their account credentials</strong></li>
                  <li>Log this action in the admin audit trail</li>
                </ul>
                <p className="text-red-200 text-sm mt-3 font-medium">
                  🚨 You will be responsible for securely sharing this password with the user and ensuring they change it immediately.
                </p>
              </div>
            </div>
          </div>

          {/* User Details */}
          <div className="bg-gray-900/50 p-4 rounded-lg">
            <div className="flex items-center space-x-3 mb-3">
              <User className="w-5 h-5 text-white" />
              <h4 className="font-medium text-white">User to Reset</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p><span className="text-gray-400">Email:</span></p>
                <p className="text-white font-medium">{user.email}</p>
              </div>
              <div>
                <p><span className="text-gray-400">Name:</span></p>
                <p className="text-white font-medium">{user.full_name || 'Not provided'}</p>
              </div>
              <div>
                <p><span className="text-gray-400">Username:</span></p>
                <p className="text-white font-medium">{user.username || 'Not set'}</p>
              </div>
              <div>
                <p><span className="text-gray-400">Member Since:</span></p>
                <p className="text-white font-medium">{new Date(user.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Reason for Password Reset (Required for audit trail)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              placeholder="Enter reason for password reset (e.g., user forgot password, security concern, user request, etc.)"
              required
            />
          </div>

          {/* Confirmation */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Type <span className="font-bold text-blue-400 bg-blue-900/30 px-2 py-1 rounded">RESET PASSWORD</span> to confirm this action
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="RESET PASSWORD"
            />
          </div>

          {/* Final Notice */}
          <div className="bg-yellow-900/50 border border-yellow-700 p-4 rounded-lg">
            <p className="text-yellow-200 text-sm text-center font-medium">
              🔐 A new password will be generated and shown to you. Share it securely with the user.
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-end gap-4 pt-6 border-t border-gray-700">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-6 py-2 text-gray-300 hover:text-white disabled:opacity-50 border border-gray-600 rounded-md hover:bg-gray-700/50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleReset}
              disabled={loading || !isConfirmValid || !reason.trim()}
              className="flex items-center justify-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              <Key className="w-4 h-4" />
              <span>{loading ? 'Sending Reset Email...' : 'Reset Password'}</span>
              <span>{loading ? 'Generating New Password...' : 'Generate New Password'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}